import React, { FC, useEffect, useState } from 'react';
import { Subscription } from 'rxjs';
import classnames from 'classnames';

import { unsubscribeComponent } from 'utils/unsubscribeComponent';
import { getSystemOption } from 'api/postitivePay/service';
import { getSystemPublicOption } from 'api/config/service';
import useLocale from 'components/useLocale';
import { SYSTEM_OPTIONS } from 'constants.js';

import { FDIC_LOGO_OPTIONS, FDIC_LOGO_VARIANTS } from './FdicLogo.constants';
import useStyles from './FdicLogo.styles';
import FdicLogoLight from './FdicLogoWhite.svg';
import FdicLogoDark from './FdicLogoBlue.svg';
import { OptionType, LogoMapType } from './FdicLogo.types';

const { FDIC_LOGO_HEADER, FDIC_LOGO_LOGIN } = SYSTEM_OPTIONS;
const { FDIC_DARK, FDIC_LIGHT, FDIC_NONE } = FDIC_LOGO_OPTIONS;
const { HEADER, LOGIN } = FDIC_LOGO_VARIANTS;

interface Props {
  type: typeof HEADER | typeof LOGIN;
  className?: string;
}

const OPTION_ID_MAP = {
  header: FDIC_LOGO_HEADER,
  login: FDIC_LOGO_LOGIN
};

const subscription: Record<string, Subscription | null> = {
  getSystemOption: null
};

const FdicLogo: FC<Props> = ({ className, type }) => {
  const locale = useLocale();
  const [fdicOption, setFdicOption] = useState<OptionType>();
  const { dark, fdicContainer, fdicLogo, light } = useStyles();

  const LOGO_MAP: LogoMapType = {
    [FDIC_DARK]: { logoClass: dark, logoSrc: FdicLogoDark },
    [FDIC_LIGHT]: { logoClass: light, logoSrc: FdicLogoLight }
  };

  useEffect(() => {
    const service = type === LOGIN ? getSystemPublicOption : getSystemOption;

    subscription.getSystemOption = service({
      optId: OPTION_ID_MAP[type]
    }).subscribe(({ data: option, success }) => {
      if (success) {
        setFdicOption(option);
      }
    });

    return () => unsubscribeComponent(subscription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!fdicOption || fdicOption === FDIC_NONE) {
    return <></>;
  }

  const { logoClass = '', logoSrc = '' } = LOGO_MAP[fdicOption] || {};

  return (
    <div data-qa="fdic-logo-container" className={classnames(logoClass, fdicContainer, className)}>
      <img
        className={fdicLogo}
        alt={locale['fdicLogo.logoAlt']}
        src={logoSrc}
        data-qa="fdic-logo"
      />
      <span data-qa="fdic-wordmark">{locale['fdicLogo.wordmark']}</span>
    </div>
  );
};

export default FdicLogo;
